import { useState, useEffect, useMemo } from "react";
import {
  MessageSquareText, ChevronRight, ArrowLeft, RotateCcw, Copy, Check,
  Pencil, Trash2, Plus, X, Settings, MessageCircle, Flag, GitBranch,
  Image as ImageIcon, UploadCloud, Loader2, Wrench,
} from "lucide-react";
import { storageService } from "@/lib/storage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { chatGuideApi } from "@/lib/unifiedApi";
import { logActivity } from "@/lib/activityLog";
import { useAuth } from "@/context/AuthContext";

// A node can carry BOTH a reply (answer_text, shown with a copy button) and
// options (buttons that lead to the next node) — so one step can "send this
// message, then choose what's next".

// ─── Navigator (walk the decision tree) ─────────────────────
function Navigator({ nodes, nodesById }) {
  const root = useMemo(() => nodes.find((n) => n.root) || null, [nodes]);
  const [currentId, setCurrentId] = useState(null);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => { setCurrentId(root?.id || null); setHistory([]); }, [root?.id]);

  const current = currentId ? nodesById[currentId] : root;

  if (!root) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl py-16 text-center text-slate-400">
        ยังไม่มีไกด์ — admin เพิ่มได้ที่โหมด "แก้ไข"
      </div>
    );
  }
  if (!current) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-6 text-center text-slate-500">
        ไม่พบขั้นตอนถัดไป (อาจถูกลบ)
        <div className="mt-3">
          <Button onClick={() => { setCurrentId(root.id); setHistory([]); }} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            <RotateCcw size={15} className="mr-1" />เริ่มใหม่
          </Button>
        </div>
      </div>
    );
  }

  const options = current.options || [];
  const pick = (nextId) => { setHistory((h) => [...h, current.id]); setCurrentId(nextId); setCopied(false); };
  const back = () => { setHistory((h) => { const n = [...h]; const prev = n.pop(); setCurrentId(prev ?? root.id); return n; }); setCopied(false); };
  const restart = () => { setCurrentId(root.id); setHistory([]); setCopied(false); };
  const copy = async () => {
    try { await navigator.clipboard.writeText(current.answer_text || ""); setCopied(true); toast.success("คัดลอกแล้ว"); setTimeout(() => setCopied(false), 2000); }
    catch { toast.error("คัดลอกไม่สำเร็จ"); }
  };

  const crumbs = history.map((id) => nodesById[id]?.title).filter(Boolean);

  return (
    <div className="space-y-3">
      {crumbs.length > 0 && (
        <div className="text-xs text-slate-400 flex items-center gap-1 flex-wrap">
          {crumbs.map((t, i) => (
            <span key={i} className="flex items-center gap-1">{t}<ChevronRight size={11} /></span>
          ))}
          <span className="text-slate-500">{current.title}</span>
        </div>
      )}

      {/* Internal guide / how-to-check (not sent to customer) */}
      {current.note && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
          <div className="text-sm text-amber-700 mb-2.5 flex items-center gap-1.5">
            <Wrench size={16} />วิธีเช็ค / สำหรับแอดมิน
          </div>
          <div className="text-[17px] text-slate-800 whitespace-pre-wrap leading-7">
            {current.note}
          </div>
        </div>
      )}

      {/* Reply to send (if any) */}
      {current.answer_text && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
          <div className="text-sm text-emerald-700 mb-2.5 flex items-center gap-1.5">
            <MessageCircle size={16} />ข้อความที่ส่งลูกค้า
          </div>
          <div className="bg-white rounded-xl p-4 text-[17px] text-slate-800 whitespace-pre-wrap leading-7 mb-4">
            {current.answer_text}
          </div>
          <Button onClick={copy} className="h-auto py-3 px-6 text-base" style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            {copied ? <><Check size={18} className="mr-1.5" />คัดลอกแล้ว</> : <><Copy size={18} className="mr-1.5" />คัดลอกข้อความ</>}
          </Button>
        </div>
      )}

      {/* Step images (if any) */}
      {(current.images || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
          <div className="text-xs text-slate-400 flex items-center gap-1.5"><ImageIcon size={13} />รูปประกอบขั้นตอน (คลิกเพื่อดูเต็ม)</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {current.images.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                <img src={url} alt={`ขั้นตอน ${i + 1}`} className="w-full rounded-lg border border-slate-200 hover:opacity-90" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Options to continue (if any) — big icon cards */}
      {options.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-6">
          {current.answer_text && <div className="text-sm text-slate-400 mb-1 text-center">ขั้นต่อไป</div>}
          <div className="text-2xl font-medium text-slate-800 mb-5 text-center">{current.title}</div>
          <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))" }}>
            {options.map((o, i) => {
              const target = o.next ? nodesById[o.next] : null;
              const Icon = target && (target.options || []).length > 0 ? GitBranch : MessageCircle;
              return (
                <button key={i} onClick={() => pick(o.next)} disabled={!o.next}
                  className="border border-slate-200 rounded-2xl px-4 py-6 flex flex-col items-center gap-3 text-center hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-40">
                  <Icon size={30} className="text-blue-600" />
                  <span className="text-[17px] text-slate-800 leading-snug">{o.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!current.answer_text && !current.note && options.length === 0 && (current.images || []).length === 0 && (
        <div className="bg-white border border-slate-200 rounded-2xl p-5 text-slate-400 text-sm">
          ขั้นตอนนี้ยังไม่มีข้อความ ตัวเลือก หรือรูป (admin เพิ่มในโหมดแก้ไข)
        </div>
      )}

      <div className="flex items-center gap-5 text-base text-slate-500 pt-1">
        {history.length > 0 && (
          <button onClick={back} className="flex items-center gap-1.5 hover:text-slate-700"><ArrowLeft size={16} />ย้อนกลับ</button>
        )}
        <button onClick={restart} className="flex items-center gap-1.5 hover:text-slate-700"><RotateCcw size={16} />เริ่มใหม่</button>
      </div>
    </div>
  );
}

// ─── Node edit dialog ───────────────────────────────────────
const EMPTY = { title: "", answer_text: "", note: "", options: [], images: [] };

function NodeDialog({ open, onClose, node, nodes }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    setForm(node
      ? { title: node.title || "", answer_text: node.answer_text || "", note: node.note || "", options: (node.options || []).map((o) => ({ ...o })), images: [...(node.images || [])] }
      : { ...EMPTY, options: [], images: [] });
  }, [node, open]);

  async function uploadImage(file) {
    if (!file) return;
    if (!/^image\//.test(file.type)) { toast.error("เลือกไฟล์รูปภาพ"); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("รูปใหญ่เกิน 10MB"); return; }
    setUploading(true);
    try {
      const res = await storageService.uploadFile(file, `chat_guide/${Date.now()}`);
      setForm((f) => ({ ...f, images: [...f.images, res.url] }));
    } catch (e) { console.error(e); toast.error("อัปโหลดรูปไม่สำเร็จ"); }
    finally { setUploading(false); }
  }
  const rmImage = (i) => setForm((f) => ({ ...f, images: f.images.filter((_, idx) => idx !== i) }));

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setOpt = (i, k, v) => setForm((f) => { const options = f.options.map((o, idx) => idx === i ? { ...o, [k]: v } : o); return { ...f, options }; });
  const addOpt = () => setForm((f) => ({ ...f, options: [...f.options, { label: "", next: "" }] }));
  const rmOpt = (i) => setForm((f) => ({ ...f, options: f.options.filter((_, idx) => idx !== i) }));

  async function save() {
    if (!form.title.trim()) { toast.error("กรุณากรอกหัวข้อขั้นตอน"); return; }
    const cleanOpts = form.options.filter((o) => o.label.trim()).map((o) => ({ label: o.label.trim(), next: o.next || "" }));
    if (!form.answer_text.trim() && !form.note.trim() && cleanOpts.length === 0 && form.images.length === 0) { toast.error("ต้องมีข้อความ วิธีเช็ค ตัวเลือก หรือรูป อย่างน้อยหนึ่งอย่าง"); return; }
    setSaving(true);
    try {
      const data = { title: form.title.trim(), answer_text: form.answer_text.trim(), note: form.note.trim(), options: cleanOpts, images: form.images };
      if (node?.id) {
        await chatGuideApi.update(node.id, data);
        logActivity(user?.id, user?.name, "chatguide_update", `แก้ไขขั้นตอน: ${data.title}`);
        toast.success("บันทึกแล้ว");
      } else {
        await chatGuideApi.create({ ...data, root: nodes.length === 0 });
        logActivity(user?.id, user?.name, "chatguide_update", `เพิ่มขั้นตอน: ${data.title}`);
        toast.success("เพิ่มแล้ว");
      }
      onClose();
    } catch (e) { console.error(e); toast.error("บันทึกไม่สำเร็จ"); }
    finally { setSaving(false); }
  }

  const otherNodes = nodes.filter((n) => n.id !== node?.id);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white border-slate-200 text-slate-800 max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{node?.id ? "แก้ไขขั้นตอน" : "เพิ่มขั้นตอน"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label className="text-slate-500">หัวข้อขั้นตอน <span className="text-red-500">*</span></Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="เช่น ตู้มีปัญหา / เลือกสาขา / แจ้งกลุ่ม Qottontale"
              className="bg-white border-slate-200 text-slate-800" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ข้อความที่ส่งลูกค้า / คำแนะนำ (ถ้ามี)</Label>
            <textarea value={form.answer_text} onChange={(e) => set("answer_text", e.target.value)}
              rows={4} placeholder="ข้อความให้ copy ส่งลูกค้า หรือคำสั่งภายใน เช่น แจ้ง Line กลุ่ม [FYM-PN]…"
              className="w-full rounded-md bg-white border border-slate-200 text-slate-800 text-sm p-2 resize-none" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">วิธีเช็ค / คำแนะนำภายใน (ไม่ได้ส่งลูกค้า)</Label>
            <textarea value={form.note} onChange={(e) => set("note", e.target.value)}
              rows={4} placeholder="ขั้นตอนให้แอดมินเช็ค เช่น 1. เปิดเครื่องปรินท์ไหม 2. เช็ค Status App…"
              className="w-full rounded-md bg-white border border-slate-200 text-slate-800 text-sm p-2 resize-none" />
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">ตัวเลือกต่อ (ถ้ามี)</Label>
            <div className="space-y-2">
              {form.options.map((o, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Input value={o.label} onChange={(e) => setOpt(i, "label", e.target.value)}
                    placeholder="ข้อความปุ่ม" className="bg-white border-slate-200 text-slate-800 flex-1" />
                  <Select value={o.next || "none"} onValueChange={(v) => setOpt(i, "next", v === "none" ? "" : v)}>
                    <SelectTrigger className="bg-white border-slate-200 text-slate-800 w-40"><SelectValue placeholder="ไปขั้นตอน…" /></SelectTrigger>
                    <SelectContent className="bg-white border-slate-200 text-slate-800 max-h-56">
                      <SelectItem value="none">— ยังไม่ผูก —</SelectItem>
                      {otherNodes.map((n) => (
                        <SelectItem key={n.id} value={n.id}>{n.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button onClick={() => rmOpt(i)} className="text-slate-400 hover:text-rose-500 p-1"><X size={16} /></button>
                </div>
              ))}
              <button onClick={addOpt} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"><Plus size={14} />เพิ่มตัวเลือก</button>
            </div>
            <p className="text-xs text-slate-400">* สร้างขั้นตอนปลายทางไว้ก่อน แล้วค่อยเลือกผูกในช่อง "ไปขั้นตอน"</p>
          </div>

          <div className="space-y-2">
            <Label className="text-slate-500">รูปประกอบ (สอนขั้นตอน — ถ้ามี)</Label>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {form.images.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt="" className="w-full h-20 object-cover rounded-lg border border-slate-200" />
                    <button onClick={() => rmImage(i)}
                      className="absolute -top-1.5 -right-1.5 bg-rose-500 text-white rounded-full p-0.5 shadow">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 cursor-pointer">
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={uploading} />
              {uploading ? <><Loader2 size={14} className="animate-spin" />กำลังอัปโหลด...</> : <><UploadCloud size={14} />เพิ่มรูป</>}
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="text-slate-500">ยกเลิก</Button>
          <Button onClick={save} disabled={saving} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
            {saving ? "กำลังบันทึก..." : "บันทึก"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Editor (manage nodes) ──────────────────────────────────
function Editor({ nodes }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const { user } = useAuth();

  async function setRoot(node) {
    try {
      const current = nodes.filter((n) => n.root && n.id !== node.id);
      for (const n of current) await chatGuideApi.update(n.id, { root: false });
      await chatGuideApi.update(node.id, { root: true });
      toast.success(`ตั้ง "${node.title}" เป็นจุดเริ่มต้น`);
    } catch { toast.error("ทำรายการไม่สำเร็จ"); }
  }

  async function remove(node) {
    if (!window.confirm(`ลบขั้นตอน "${node.title}" ?`)) return;
    try {
      await chatGuideApi.delete(node.id);
      logActivity(user?.id, user?.name, "chatguide_update", `ลบขั้นตอน: ${node.title}`);
      toast.success("ลบแล้ว");
    } catch { toast.error("ลบไม่สำเร็จ"); }
  }

  const shown = nodes.filter((n) => !search.trim() || (n.title || "").toLowerCase().includes(search.trim().toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="ค้นหาขั้นตอน"
          className="bg-white border-slate-200 text-slate-800 max-w-xs" />
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} style={{ backgroundColor: "#2563eb", color: "#fff" }}>
          <Plus size={16} className="mr-1" />เพิ่มขั้นตอน
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {shown.length === 0 ? (
          <div className="py-12 text-center text-slate-400">{nodes.length === 0 ? 'ยังไม่มีขั้นตอน — กด "เพิ่มขั้นตอน"' : "ไม่พบขั้นตอนที่ค้นหา"}</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {shown.map((n) => {
              const hasOpts = (n.options || []).length > 0;
              return (
                <div key={n.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50">
                  <div className="flex-shrink-0">
                    {hasOpts ? <GitBranch size={18} className="text-blue-600" /> : <MessageCircle size={18} className="text-emerald-600" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-slate-800 font-medium truncate flex items-center gap-2">
                      {n.title}
                      {n.root && <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5 flex items-center gap-0.5"><Flag size={9} />เริ่มต้น</span>}
                    </div>
                    <div className="text-xs text-slate-400 truncate">
                      {n.answer_text ? "มีข้อความ" : ""}{n.answer_text && hasOpts ? " · " : ""}{hasOpts ? `${n.options.length} ตัวเลือก` : (!n.answer_text ? "ว่าง" : "")}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {!n.root && (
                      <button onClick={() => setRoot(n)} className="p-2 rounded-lg hover:bg-amber-50 text-slate-400 hover:text-amber-600" title="ตั้งเป็นจุดเริ่มต้น">
                        <Flag size={15} />
                      </button>
                    )}
                    <button onClick={() => { setEditing(n); setDialogOpen(true); }} className="p-2 rounded-lg hover:bg-slate-100 text-slate-400" title="แก้ไข">
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => remove(n)} className="p-2 rounded-lg hover:bg-rose-50 text-slate-400 hover:text-rose-500" title="ลบ">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <NodeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} node={editing} nodes={nodes} />
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────
export default function ChatGuidePage() {
  const { isAdmin } = useAuth();
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("nav"); // nav | edit

  useEffect(() => {
    const unsub = chatGuideApi.subscribe((data) => {
      data.sort((a, b) => (a.title || "").localeCompare(b.title || "", "th"));
      setNodes(data);
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, []);

  const nodesById = useMemo(() => {
    const m = {};
    nodes.forEach((n) => { m[n.id] = n; });
    return m;
  }, [nodes]);

  return (
    <div className="min-h-screen bg-slate-100 p-4 md:p-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <MessageSquareText size={24} className="text-blue-600" />Chat Guide
          </h1>
          <p className="text-slate-400 text-sm mt-0.5">ไกด์ตอบแชท — กดเลือกไปทีละขั้นจนได้คำตอบที่ควรส่งลูกค้า</p>
        </div>
        {isAdmin() && (
          <div className="flex bg-white border border-slate-200 rounded-lg overflow-hidden">
            <button onClick={() => setMode("nav")}
              className={`px-4 py-2 text-sm flex items-center gap-1.5 ${mode === "nav" ? "bg-blue-600 text-white" : "text-slate-500"}`}>
              <MessageSquareText size={15} />ใช้งาน
            </button>
            <button onClick={() => setMode("edit")}
              className={`px-4 py-2 text-sm flex items-center gap-1.5 ${mode === "edit" ? "bg-blue-600 text-white" : "text-slate-500"}`}>
              <Settings size={15} />แก้ไข
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center text-slate-400 py-16">กำลังโหลด...</div>
      ) : mode === "edit" && isAdmin() ? (
        <Editor nodes={nodes} />
      ) : (
        <Navigator nodes={nodes} nodesById={nodesById} />
      )}
    </div>
  );
}
